import * as React from 'react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '~/client/components/ui/dropdown-menu'
import { Button } from '~/client/components/ui/button'
import { BookMarked } from 'lucide-react'
const ChevronDownIcon = ({ size = 20, className, ...props }: React.SVGProps<SVGSVGElement> & { size?: number | string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} {...props}>
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ChevronUpIcon = ({ size = 20, className, ...props }: React.SVGProps<SVGSVGElement> & { size?: number | string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} {...props}>
    <path d="M18 15L12 9L6 15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const PlusIcon = ({ size = 16, className, ...props }: React.SVGProps<SVGSVGElement> & { size?: number | string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} {...props}>
    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export type SharedListItem = {
  id: string
  title: string
  spaceCount: number
  slug: string
  venueGroupName?: string
  lastUpdatedAt?: string
}

export type SharedListsDropdownProps = {
  options?: SharedListItem[]
  isLoading?: boolean
  onSelect?: (list: SharedListItem) => void
  onCreateNew?: () => void
  triggerClassName?: string
}

export default function SharedListsDropdown({ options, isLoading, onSelect, onCreateNew, triggerClassName }: SharedListsDropdownProps) {
  const items = options ?? []
  const [open, setOpen] = React.useState(false)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="revelOutline" size="revel36" className={`rounded-[8px] pl-4 pr-3 ${triggerClassName ?? ''}`}>
          <span className="text-[#8a3dff]">Collections</span>
          {open ? <ChevronUpIcon className="size-5" /> : <ChevronDownIcon className="size-5" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        collisionPadding={16}
        className="w-[440px] max-w-[calc(100vw-32px)] rounded-[12px] !p-0 bg-white border border-neutral-200 shadow-[0px_4px_4px_10px_rgba(0,0,0,0.04)] flex flex-col"
      >
        {/* Scrollable list */}
        <div className="max-h-[400px] overflow-y-auto rounded-t-[12px]">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <React.Fragment key={`skeleton-${idx}`}>
                <div className="px-4 py-[18px]">
                  <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
                  <div className="mt-2 h-4 w-1/2 bg-muted/70 animate-pulse rounded" />
                </div>
                {idx < 3 ? <div className="h-px bg-[#e4e1e9]" /> : null}
              </React.Fragment>
            ))
          ) : items.length > 0 ? (
            items.map((list, idx) => (
              <React.Fragment key={list.id}>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault()
                    onSelect?.(list)
                    setOpen(false)
                  }}
                  className="px-4 py-[18px] cursor-pointer rounded-none hover:bg-[rgba(138,61,255,0.06)] data-[highlighted]:bg-[rgba(138,61,255,0.06)] data-[highlighted]:text-[#55534e]"
                >
                  <div className="flex flex-col w-full">
                    <div className="text-[14px] font-medium text-[#55534e]">{list.title}</div>
                    <div className="mt-1 text-[14px] font-medium text-[#9b9b9a] flex items-center gap-1.5">
                      {list.venueGroupName && (
                        <>
                          <span>{list.venueGroupName}</span>
                          <span className="text-[#c8c5cc]">•</span>
                        </>
                      )}
                      <span>{list.spaceCount} {list.spaceCount === 1 ? 'space' : 'spaces'}</span>
                      {list.lastUpdatedAt && (
                        <>
                          <span className="text-[#c8c5cc]">•</span>
                          <span>Updated {list.lastUpdatedAt}</span>
                        </>
                      )}
                    </div>
                  </div>
                </DropdownMenuItem>
                {idx < items.length - 1 ? <DropdownMenuSeparator className="h-px bg-[#e4e1e9] mx-0" /> : null}
              </React.Fragment>
            ))
          ) : (
            <div className="flex flex-col items-center py-8 px-2">
              <div className="flex items-center justify-center size-14 rounded-full bg-[#f4f0fa] mb-4">
                <BookMarked className="size-6 text-[#8a3dff]" />
              </div>
              <div className="text-[15px] font-semibold text-[#18003d] mb-1">Build your first Collection</div>
              <p className="text-[13px] text-[#95919a] text-center leading-[1.4] max-w-[260px]">
                Create a curated list of venues to share with colleagues or use to jump start your event planning.
              </p>
            </div>
          )}
        </div>

        {/* Sticky Create button */}
        <div className="sticky bottom-0 bg-white rounded-b-[12px]">
          <div className="h-px bg-[#e4e1e9]" />
          <button
            type="button"
            onClick={() => {
              onCreateNew?.()
              setOpen(false)
            }}
            className="flex w-full items-center gap-2 p-4 bg-[#8a3dff] text-white rounded-b-[12px]"
          >
            <PlusIcon className="size-4" />
            <span className="text-[14px] font-medium leading-6">Create Collection</span>
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
